import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Table,
} from "reactstrap";
import { Api } from "../../componentes/Api";

const modeloRegistro = {
    solicitudID: 0,
    citaID: 0,
    fecha: "",
    nombrePaciente: "",
    telefono: "",
    datosAdicionales: "",
    archivo: null,
    archivoNombre: "",
    adjuntoUrl: "",
};

const Coordinador = () => {
    const [listadoCalendario, setListadoCalendario] = useState([]);
    const [listadoDia, setListadoDia] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [verModal, setVerModal] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [registro, setRegistro] = useState(modeloRegistro);
    const [previewUrl, setPreviewUrl] = useState("");
    const [puedeAprobar, setPuedeAprobar] = useState(false);

    const user = JSON.parse(window.localStorage.getItem("sesion_usuario") || "null");
    const correo = user?.correo || user?.Correo || "";
    const idUsuario = user?.idUsuario || user?.IdUsuario || 0;
    const rolDescripcion = (user?.idRolNavigation?.descripcion || user?.idRolNavigation?.Descripcion || "").toString();
    const esAdmin = rolDescripcion.toLowerCase() === "administrador" || rolDescripcion.toLowerCase() === "administrador d";

    const formatKey = (date) => date.toISOString().split("T")[0];

    const buildCalendar = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const startingWeekDay = firstDay.getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const cells = [];
        for (let i = 0; i < startingWeekDay; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    };

    const cells = useMemo(() => buildCalendar(currentMonth), [currentMonth]);

    const obtenerDatosCalendario = useCallback(async () => {
        let response = null;

        if (correo) {
            response = await fetch(Api + "Citas/ListaFechaCirugiaDisponible/" + encodeURIComponent(correo));
        }

        // Fallback al endpoint general para no dejar el calendario sin cantidad.
        if (!response || !response.ok) {
            response = await fetch(Api + "Citas/ListaFechaCirugiaDisponible");
        }

        if (response.ok) {
            const data = await response.json();
            setListadoCalendario(Array.isArray(data) ? data : []);
            return;
        }

        setListadoCalendario([]);
    }, [correo]);

    const obtenerRegistrosDia = useCallback(async (fecha) => {
        if (!correo) return;
        const response = await fetch(Api + "Citas/Coordinador/Lista/" + formatKey(fecha) + "/" + correo);
        if (response.ok) {
            const data = await response.json();
            setListadoDia(data);
        } else {
            setListadoDia([]);
        }
    }, [correo]);

    const obtenerPermisos = useCallback(async () => {
        if (!idUsuario) {
            setPuedeAprobar(esAdmin);
            return;
        }

        const response = await fetch(Api + "Administracion/GetAsignacionModulo/" + idUsuario);
        if (!response.ok) {
            setPuedeAprobar(esAdmin);
            return;
        }

        const data = await response.json();
        const apruebaCoordinador = (Array.isArray(data) ? data : []).some((x) =>
            Number(x.moduloID) === 1 && Number(x.opcionMenu) === 4 && (x.aprueba === true || x.aprueba === 1)
        );
        setPuedeAprobar(esAdmin || apruebaCoordinador);
    }, [idUsuario, esAdmin]);

    useEffect(() => {
        obtenerDatosCalendario();
        obtenerRegistrosDia(selectedDate);
        obtenerPermisos();

        const intervalo = setInterval(() => {
            obtenerDatosCalendario();
            obtenerRegistrosDia(selectedDate);
        }, 15000);

        return () => clearInterval(intervalo);
    }, [obtenerDatosCalendario, obtenerRegistrosDia, obtenerPermisos, selectedDate]);

    useEffect(() => {
        if (!registro.archivo) {
            setPreviewUrl("");
            return;
        }
        const objectUrl = URL.createObjectURL(registro.archivo);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [registro.archivo]);

    const countCitas = (day) => {
        if (!day) return 0;
        const key = formatKey(day);
        return listadoCalendario.filter((c) => moment(c.fechaCirugia).format("YYYY-MM-DD") === key).map((x) => x.cantidad)[0];
    };

    const tipoDia = (day) => {
        if (!day) return "";
        const key = formatKey(day);
        return listadoCalendario.filter((c) => moment(c.fechaCirugia).format("YYYY-MM-DD") === key).map((x) => x.tipoDia)[0];
    };

    const pendienteDia = (day) => {
        if (!day) return 0;
        const key = formatKey(day);
        return listadoCalendario
            .filter((c) => moment(c.fechaCirugia).format("YYYY-MM-DD") === key)
            .map((x) => x.pendienteAprobacion ?? x.pendienteaprobracion ?? 0)[0] || 0;
    };

    const monthLabel = currentMonth.toLocaleString("es-ES", {
        month: "long",
        year: "numeric",
    });

    const seleccionarDia = (day) => {
        if (!day) return;

        const disponible = Number(countCitas(day) || 0) > 0;
        setSelectedDate(day);
        obtenerRegistrosDia(day);

        if (!disponible) {
            Swal.fire("Dia sin disponibilidad", "Selecciona un dia que tenga citas disponibles.", "info");
            return;
        }

        const pendientes = pendienteDia(day);
        setRegistro({
            ...modeloRegistro,
            fecha: formatKey(day),
            datosAdicionales: pendientes > 0 ? `Pendientes de aprobacion actuales: ${pendientes}` : "",
        });
        setVerModal(true);
    };

    const abrirNuevo = () => {
        setRegistro({
            ...modeloRegistro,
            fecha: formatKey(selectedDate),
        });
        setVerModal(true);
    };

    const abrirEditar = (item) => {
        setRegistro({
            solicitudID: item.solicitudID,
            citaID: item.citaID,
            fecha: moment(item.fecha).format("YYYY-MM-DD"),
            nombrePaciente: item.nombrePaciente || "",
            telefono: item.telefono || "",
            datosAdicionales: item.datosAdicionales || "",
            archivo: null,
            archivoNombre: item.adjuntoUrl ? item.adjuntoUrl.split("/").pop() : "",
            adjuntoUrl: item.adjuntoUrl || "",
        });
        setVerModal(true);
    };

    const confirmarSolicitud = async (item) => {
        if (!puedeAprobar) {
            Swal.fire("Sin permiso", "Tu usuario no tiene permiso de aprobador.", "warning");
            return;
        }

        const result = await Swal.fire({
            title: "Confirmar deposito/pago",
            text: "Esta accion crea la cita definitiva en ACitas.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
        });
        if (!result.isConfirmed) return;

        const response = await fetch(Api + "Citas/Coordinador/Confirmar/" + item.solicitudID + "/" + correo, {
            method: "PUT",
        });

        if (response.ok) {
            await obtenerRegistrosDia(selectedDate);
            Swal.fire("Confirmado", "La solicitud fue confirmada y enviada a citas.", "success");
            return;
        }
        const msg = await response.text();
        Swal.fire("Error", msg || "No se pudo confirmar la solicitud", "error");
    };

    const eliminarRegistro = async (item) => {
        const result = await Swal.fire({
            title: "Eliminar registro",
            text: "Esta accion no se puede deshacer",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
        });

        if (!result.isConfirmed) return;

        const response = await fetch(Api + "Citas/Coordinador/Eliminar/" + item.solicitudID + "/" + correo, {
            method: "DELETE",
        });

        if (response.ok) {
            await obtenerRegistrosDia(selectedDate);
            Swal.fire("Eliminado", "El registro fue eliminado", "success");
            return;
        }

        const msg = await response.text();
        Swal.fire("Error", msg || "No se pudo eliminar", "error");
    };

    const guardar = async () => {
        if (!registro.fecha || !registro.nombrePaciente.trim() || !registro.telefono.trim()) {
            Swal.fire("Campos requeridos", "Fecha, nombre y telefono son obligatorios", "warning");
            return;
        }

        setGuardando(true);
        try {
            const formData = new FormData();
            formData.append("fecha", registro.fecha);
            formData.append("nombrePaciente", registro.nombrePaciente);
            formData.append("telefono", registro.telefono);
            formData.append("datosAdicionales", registro.datosAdicionales || "");
            formData.append("correo", correo);
            if (registro.archivo) {
                formData.append("archivo", registro.archivo);
            }

            const isEdit = registro.solicitudID > 0;
            const endpoint = isEdit
                ? Api + "Citas/Coordinador/Editar/" + registro.solicitudID
                : Api + "Citas/Coordinador/Guardar";

            const response = await fetch(endpoint, {
                method: isEdit ? "PUT" : "POST",
                body: formData,
            });

            if (response.ok) {
                setVerModal(false);
                await obtenerRegistrosDia(new Date(registro.fecha + "T00:00:00"));
                await obtenerDatosCalendario();
                setSelectedDate(new Date(registro.fecha + "T00:00:00"));
                Swal.fire("Guardado", "Solicitud registrada con estatus SOLICITADO.", "success");
                return;
            }

            const msg = await response.text();
            Swal.fire("Error", msg || "No se pudo guardar", "error");
        } finally {
            setGuardando(false);
        }
    };

    const esPdf = (nameOrUrl) => (nameOrUrl || "").toLowerCase().endsWith(".pdf");

    return (
        <Card>
            <CardHeader style={{ backgroundColor: "#1f4e79", color: "white", fontWeight: 700 }}>
                Modulo Coordinador
            </CardHeader>
            <CardBody>
                <Row>
                    <Col md={7}>
                        <div className="calendar-wrapper" style={{ color: "#000" }}>
                            <div className="calendar-header">
                                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>{"<"}</button>
                                <h3 style={{ textTransform: "uppercase" }}>{monthLabel}</h3>
                                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>{">"}</button>
                            </div>
                            <div className="mb-2">
                                <small className="text-muted">Haz clic en un dia con disponibilidad para crear el registro.</small>
                            </div>

                            <div className="calendar-weekdays">
                                {["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"].map((d) => (
                                    <div key={d} className="weekday">
                                        {d}
                                    </div>
                                ))}
                            </div>

                            <div className="calendar-grid" style={{ color: "#000" }}>
                                {cells.map((day, idx) => {
                                    const numCitas = day ? countCitas(day) : 0;
                                    const tipo = day ? tipoDia(day) : "";
                                    const pendientes = day ? pendienteDia(day) : 0;
                                    const seleccionado = day && formatKey(day) === formatKey(selectedDate);
                                    const disponible = day ? Number(numCitas || 0) > 0 : false;

                                    return (
                                        <button
                                            key={idx}
                                            className={`calendar-day ${day ? "" : "empty"}`}
                                            style={{
                                                border: seleccionado ? "2px solid #1f4e79" : undefined,
                                                opacity: day && !disponible ? 0.6 : 1,
                                                cursor: day ? "pointer" : "not-allowed",
                                            }}
                                            onClick={() => seleccionarDia(day)}
                                            disabled={!day}
                                        >
                                            {day && (
                                                <>
                                                    <span className="day-number" style={{ color: "#000" }}>{day.getDate()}</span>
                                                    {numCitas > 0 && (
                                                        (tipo === "Festivo" || tipo === "Congreso") ? (
                                                            <div className="citas-chip">
                                                                <small>{tipo}</small>
                                                            </div>
                                                        ) : (
                                                            <div className="citas-chip">
                                                                <span>{numCitas}</span>
                                                                <small>citas</small>
                                                            </div>
                                                        )
                                                    )}
                                                    {pendientes > 0 && (
                                                        <div style={{ marginTop: "4px", color: "#b56a00", fontSize: "11px", fontWeight: 600 }}>
                                                            Pend: {pendientes}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </Col>

                    <Col md={5}>
                        <Card>
                            <CardHeader style={{ backgroundColor: "#f8f9fc", fontWeight: 700 }}>
                                Registros del {moment(selectedDate).format("DD/MM/YYYY")}
                            </CardHeader>
                            <CardBody>
                                <Button color="success" size="sm" className="mb-3" onClick={abrirNuevo}>
                                    Nuevo Registro
                                </Button>

                                <Table bordered hover size="sm" responsive>
                                    <thead>
                                        <tr>
                                            <th>Paciente</th>
                                            <th>Telefono</th>
                                            <th>Estatus</th>
                                            <th>Adjunto</th>
                                            <th>Usuario</th>
                                            <th style={{ width: "120px" }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listadoDia.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="text-center">No hay registros en este dia</td>
                                            </tr>
                                        )}
                                        {listadoDia.map((item) => (
                                            <tr key={item.solicitudID}>
                                                <td>{item.nombrePaciente}</td>
                                                <td>{item.telefono}</td>
                                                <td>
                                                    <span className={item.estatus === "CONFIRMADO" ? "badge badge-success p-1" : "badge badge-warning p-1"}>
                                                        {item.estatus}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.adjuntoUrl ? (
                                                        <a href={item.adjuntoUrl} target="_blank" rel="noreferrer">Ver</a>
                                                    ) : "-"}
                                                </td>
                                                <td>{item.creadoPor}</td>
                                                <td>
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        className="mr-1"
                                                        onClick={() => confirmarSolicitud(item)}
                                                        disabled={item.estatus === "CONFIRMADO" || !puedeAprobar}
                                                        title={puedeAprobar ? "Confirmar" : "Requiere permiso de aprobador"}
                                                    >
                                                        <i className="fas fa-check"></i>
                                                    </Button>
                                                    <Button color="primary" size="sm" className="mr-1" onClick={() => abrirEditar(item)} disabled={item.estatus === "CONFIRMADO"}>
                                                        <i className="fas fa-pen-alt"></i>
                                                    </Button>
                                                    <Button color="danger" size="sm" onClick={() => eliminarRegistro(item)} disabled={item.estatus === "CONFIRMADO"}>
                                                        <i className="fas fa-trash-alt"></i>
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </CardBody>

            <Modal isOpen={verModal} toggle={() => setVerModal(false)}>
                <ModalHeader toggle={() => setVerModal(false)}>
                    {registro.solicitudID > 0 ? "Editar solicitud" : "Nueva solicitud"}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Dia</Label>
                        <Input
                            type="date"
                            value={registro.fecha}
                            onChange={(e) => setRegistro({ ...registro, fecha: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Nombre del paciente</Label>
                        <Input
                            value={registro.nombrePaciente}
                            onChange={(e) => setRegistro({ ...registro, nombrePaciente: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Telefono</Label>
                        <Input
                            value={registro.telefono}
                            onChange={(e) => setRegistro({ ...registro, telefono: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Datos adicionales</Label>
                        <Input
                            type="textarea"
                            rows={3}
                            value={registro.datosAdicionales}
                            onChange={(e) => setRegistro({ ...registro, datosAdicionales: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Adjuntar foto o PDF</Label>
                        <Input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            onChange={(e) => setRegistro({
                                ...registro,
                                archivo: e.target.files?.[0] || null,
                                archivoNombre: e.target.files?.[0]?.name || registro.archivoNombre,
                            })}
                        />
                        {registro.archivoNombre && <small>Archivo actual: {registro.archivoNombre}</small>}
                    </FormGroup>
                    {(previewUrl || registro.adjuntoUrl) && (
                        <FormGroup>
                            <Label>Vista previa</Label>
                            {!esPdf((registro.archivo && registro.archivo.name) || previewUrl || registro.adjuntoUrl) ? (
                                <div>
                                    <img
                                        src={previewUrl || registro.adjuntoUrl}
                                        alt="adjunto"
                                        style={{ maxWidth: "100%", maxHeight: "260px", borderRadius: "8px", border: "1px solid #ddd" }}
                                    />
                                </div>
                            ) : (
                                <div>
                                    <a href={previewUrl || registro.adjuntoUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                                        Ver PDF
                                    </a>
                                </div>
                            )}
                        </FormGroup>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={guardar} disabled={guardando}>
                        {guardando ? "Guardando..." : "Guardar"}
                    </Button>
                    <Button color="secondary" onClick={() => setVerModal(false)}>
                        Cancelar
                    </Button>
                </ModalFooter>
            </Modal>
        </Card>
    );
};

export default Coordinador;
